import { useForceUpdate } from '@kk/shared/hooks/useForceUpdate';
import { InlineNotification } from '@kk/ui/components/Notifications/InlineNotification';
import { TopLineBanner } from '@kk/ui/components/Notifications/TopLineBanner';
import type { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { appInsightsPlugin } from '#/app-insights';

class AppInsightsErrorBoundary extends React.Component<{
  onError: (message?: string) => React.ReactNode;
  children: React.ReactNode;
  appInsights: ReactPlugin;
}> {
  state = { hasError: false, message: '' };

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ hasError: true, message: error.message });
    this.props.appInsights.trackException({
      error: error,
      exception: error,
      severityLevel: SeverityLevel.Error,
      properties: { ...info },
    });
  }

  render() {
    if (this.state.hasError) {
      const { onError } = this.props;
      return typeof onError === 'function'
        ? onError(this.state.message)
        : React.createElement(onError);
    }

    return this.props.children;
  }
}

export function ErrorBoundary({
  children,
  inline = true,
  className,
}: {
  children?: React.ReactNode;
  inline?: boolean;
  className?: string;
}) {
  const forceUpdate = useForceUpdate();
  const { reset } = useQueryErrorResetBoundary();
  const { t } = useTranslation();
  return (
    <AppInsightsErrorBoundary
      onError={(_error) => {
        if (inline) {
          return (
            <InlineNotification
              data-testid="inline-error"
              type="danger"
              size="medium"
              headline={t('error.component.title')}
              message={t('error.component.message')}
              className={className}
              onDismiss={() => {
                reset();
                forceUpdate();
              }}
            />
          );
        }
        const toplineBanner = (
          <TopLineBanner
            data-testid="error-banner"
            type="danger"
            message={t('error.api.title')}
            label={t('error.api.message')}
            className={className}
            onDismiss={() => {
              reset();
              window.location.reload();
            }}
          />
        );
        const portalTarget = document.getElementById('error-portal');
        if (portalTarget) {
          return createPortal(toplineBanner, portalTarget);
        }
        return toplineBanner;
      }}
      appInsights={appInsightsPlugin}
    >
      {children}
    </AppInsightsErrorBoundary>
  );
}
